import * as React from 'react'

import { cn } from '../../lib/utils'

import type { ComponentPropsWithoutRef, ElementType } from 'react'
import { type ReactNode } from 'react'

export type TCardProps<T extends ElementType = 'div'> = {
  children: ReactNode
  className?: string
  onClick?: () => void
  as?: T
  withoutBorder?: boolean
  includeShadow?: boolean
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>

const Card = React.forwardRef<HTMLDivElement, TCardProps<ElementType | 'div'>>(
  (props, ref) => {
    const {
      children,
      className,
      as: Component = 'div',
      withoutBorder = false,
      includeShadow = false,
      ...rest
    } = props
    return (
      <Component
        ref={ref}
        data-slot='card'
        className={cn(
          'bg-neutral-surface-light flex flex-col gap-md rounded-md',
          !withoutBorder && 'border border-neutral-surface-dark p-md ',
          includeShadow && ' shadow-md shadow-black-5',
          className,
        )}
        {...rest}
      >
        {children}
      </Component>
    )
  },
)

Card.displayName = 'Card'

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-header'
      className={cn(
        'grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-title'
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-description'
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-action'
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-content'
      className={cn('px-6', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-footer'
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
}
